import React, { useEffect, useMemo } from "react";
import LightThemePage from "../../components/layout/LightThemePage";
import { useSearchParams } from "react-router-dom";
import { useGallopData } from "../../hooks/useGallopData";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { every, isEmpty, toNumber } from "lodash";
import { Box, Container, CssBaseline, Stack, Typography } from "@mui/material";
import KeyParamTable from "../../components/gallops/KeyParamTable";
import SplitsTable from "../../components/gallops/SplitsTable";
import { useAuth } from "../../hooks/useAuth";
import dayjs from "dayjs";
import useHubspotTracking from "../../hooks/useHubspotTracking";
import GallopGroups from "../../components/gallops/GallopGroups";
import { t } from "i18next";

function GallopPreview() {
  let [searchParams] = useSearchParams();

  const { setToken, getUser, isUserFetching } = useAuth();

  //To hide chat banner in this page
  useHubspotTracking();

  //expected data from query params
  // token:user jwt
  // trainings: 10, 12
  //user:str
  //horse:str
  //start_date:date
  //end_date:date
  const trainings = searchParams.get("trainings");
  const user = searchParams.get("user");
  const horse = searchParams.get("horse");
  const start_date = searchParams.get("start_date");
  const end_date = searchParams.get("end_date");
  const sorting = searchParams.get("sorting");

  //query params
  const TOKEN = searchParams.get("token");
  //Effect

  useEffect(() => {
    //if token defined set
    if (TOKEN) {
      setToken(TOKEN);
      getUser();
    }
  }, [TOKEN]);

  const trainingsAsArray = useMemo(() => {
    if (!trainings) return [];

    //Split trainings by ,
    let splitedTrainings = trainings.split(",");

    return splitedTrainings.map(
      (training) => `${encodeURIComponent("trainings[]")}=${training}`,
    );
  }, [trainings]);

  // max-splits-distance: 1600 int
  const maxSplitDistance = searchParams.get("max_splits_distance")
    ? toNumber(searchParams.get("max_splits_distance"))
    : null;

  const { data,isLoadingGallopData } = useGallopData({
    gallopDataQueryEnabled: !isUserFetching,
    options: [
      ...trainingsAsArray,
      ...(maxSplitDistance ? [`max_splits_distance=${maxSplitDistance}`] : []),
      ...(user ? [`user=${user}`] : []),
      ...(horse ? [`horse=${horse}`] : []),
      ...(start_date ? [`start_date=${start_date}`] : []),
      ...(end_date ? [`end_date=${end_date}`] : []),
      ...(sorting ? [`sorting=${sorting}`] : []),
    ],
  });

  const _trainings = data?.trainings || [];

  const gallopGroups = data?.grouped_trainings || [];

  // Get the first horse name and date safely
  const firstHorseName = _trainings?.[0]?.horse?.name;
  const firstDate = dayjs(_trainings?.[0]?.creation_date).format("YYYY-MM-DD");

  // Check if all trainings have the same horse name
  const allSameHorse =
    !!firstHorseName &&
    every(_trainings, (t) => t?.horse?.name === firstHorseName);

  // Check if all trainings have the same date (ignoring time)
  const allSameDate =
    !!firstDate &&
    every(
      _trainings,
      (t) => dayjs(t?.creation_date).format("YYYY-MM-DD") === firstDate,
    );

  // Compute result safely
  const result = allSameHorse ? firstHorseName : allSameDate ? firstDate : "";

  const RenderPageTitle = () => {
    return (
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <div className="gallopTitle">
          <Typography fontSize={20} fontWeight={600} color={"white"}>
            {t("gallop:repportTitle")} {result}
          </Typography>
        </div>
        <img
          style={{
            objectFit: "contain",
            width: 80,
            height: 80,
            marginRight: 10,
          }}
          src="/arioneo_logo_white.png"
          alt="arioneo logo"
        />
      </Box>
    );
  };

  return (
    <LightThemePage>
      <CssBaseline />
      <div
        data-testid="gallop-review-content"
        data-loaded={!isUserFetching && !isLoadingGallopData}
      >
        {isEmpty(gallopGroups) ? (
          <>
            <RenderPageTitle />
            <Stack spacing={1} p={1}>
              <Typography color={"gray"} variant="caption">
                {t("splits:explication2")}
              </Typography>
              <SplitsTable data={_trainings} allSameDate={allSameDate} />
              <Typography color={"gray"} variant="caption">
                {t("gallop:explication1")}
              </Typography>
              <KeyParamTable data={_trainings} allSameDate={allSameDate} />
            </Stack>
          </>
        ) : (
          <GallopGroups groups={gallopGroups} trainings={_trainings} />
        )}
      </div>
    </LightThemePage>
  );
}

export default GallopPreview;
